<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-cancel="hide()"
    :maxWidth="modalWidth"
    :showConfirmButton="false"
  >
    <div v-if="diagrams.length == 1" class="mt-2">
      <flowchart :nodes="diagrams[0].nodes"
                 :connections="diagrams[0].connections"
                 :remove-requires-confirmation="true"
                 :width="maxWidth(diagrams[0])"
                 :height="maxHeight(diagrams[0])"
                 ref="chart"
                 @render="handleRender"
                 :readonly="true"
                 :readOnlyPermissions="{ allowDragNodes: true, allowSave: false, allowAddNodes: false, allowEditNodes: false, allowEditConnections: false, allowDblClick: false, allowRemove: false }"
                 @nodesdragged="dragNodeFinished">
      </flowchart>
    </div>
    <div v-if="diagrams.length > 1">
      <v-tabs centered v-model="selectedTab" @change="onChangeTab">
        <v-tab v-for="(diagram, index) in diagrams" :key="index">{{diagram.flowName}}</v-tab>
        <v-tab-item v-for="(diagram, index) in diagrams" :key="index" class="mt-2" :eager="true">
          <flowchart :id="'diagram' + index"
                     :nodes="diagram.nodes"
                     :connections="diagram.connections"
                     :remove-requires-confirmation="true"
                     width="100%"
                     :height="maxHeight(diagram)"
                     @render="handleRender"
                     v-if="selectedTab === index"
                     :readonly="true"
                     :readOnlyPermissions="{ allowDragNodes: true, allowSave: false, allowAddNodes: false, allowEditNodes: false, allowEditConnections: false, allowDblClick: false, allowRemove: false }"
                     @nodesdragged="dragNodeFinished">
          </flowchart>
        </v-tab-item>
      </v-tabs>
    </div>
    <template v-slot:actions>
      <div style="position: absolute" v-if="fromDocument">
        <span class="ml-2"><v-icon class="mr-1" color="#C8E6C9" small>mdi-square</v-icon>{{ $t("Executed") }}</span>
        <span class="ml-2"><v-icon class="mr-1" color="#BBDEFB" small>mdi-square</v-icon>{{ $t("InProgress") }}</span>
        <span class="ml-2"><v-icon class="mr-1" small>mdi-square-outline</v-icon>{{ $t("Pending") }}</span>
      </div>
    </template>
  </base-modal>
</template>
<script>
import FlowDiagram from "@/ecm/models/workflow/FlowDiagram";
import Vue from 'vue';
import FlowChart from 'flowchart-vue';

Vue.use(FlowChart);

export default {
  name: "FlowDiagramModal",
  components: {
  },
  data() {
    return {
      modal: {
        title: this.$t("FlowChart"),
        key: "flowDiagramModal",
      },
      open: false,
      fromDocument: false,
      diagrams: [],
      selectedTab: 0
    };
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return this.diagrams.length === 1
        ? this.$t("FlowChart") + " - " + this.diagrams[0].flowName
        : this.$t("FlowChart");
    },
    modalWidth() {
      return "70%";
    },
  },
  methods: {
    maxHeight(diagram) {
      return this.getHeightForContainer(diagram.nodes);
    },
    maxWidth(diagram) {
        return this.getWidthForContainer(diagram.nodes);
    },
    getContentTypes(id) {
      FlowService.getAvailableContentTypes(id).then((data) => {
        this.contentTypesList = data;
      });
    },
    fromFlow(flowName, flowId, states) {
      let diagramFlow = new FlowDiagram();
      diagramFlow.flowId = flowId;
      diagramFlow.buildFrom(states, (document.body.clientWidth - 130) * 0.7);
      diagramFlow.flowName = flowName;

      this.readSettingsForDiagram(diagramFlow);

      this.diagrams = [diagramFlow];
      this.fromDocument = false;

      this.show();
    },
    fromDocumentList(flowDiagramList) {
      this.fromDocument = true;

      let diagram = flowDiagramList.map((x) => new FlowDiagram(x));

      this.readSettingsForDiagram(diagram[this.selectedTab]);

      this.diagrams = diagram;

      this.show();
    },
    show() {
      this.open = true;
      this.selectedTab = 0;
    },
    hide() {
      this.open = false;
      this.selectedTab = 0;
    },
    handleRender(node, children) {
      children.body.style("fill", node.color);
      children.body.style("display", "none");

      children.title._groups[0][0].textContent = node.name;

      if (node.approvers.length > 0)
        children.content._groups[0][0].textContent = node.approvers[0].name;

      try {
        let title = children.body._groups[0][0].parentNode.getElementsByClassName("title")[0];
        let text = children.body._groups[0][0].parentNode.getElementsByTagName("text")[1];

        title.style.fill = node.color;
        title.style.height = 60;
        title.style.display = "block";
        title.style.textAlign = "center";

        text.style.fontSize = "12px";
      }
      catch (ex) {
        // silence this exception because it's only for styling
      }
    },
    dragNodeFinished(movedNodes) {
        movedNodes.forEach(movedNode => {
            let nodeInDiagramIndex = this.diagrams[this.selectedTab].nodes.findIndex(t => t.id === movedNode.id);

            this.diagrams[this.selectedTab].nodes[nodeInDiagramIndex].x = movedNode.x;
            this.diagrams[this.selectedTab].nodes[nodeInDiagramIndex].y = movedNode.y;
        })

        this.diagrams[this.selectedTab].sortNodes();

        localStorage.setItem(`node-${this.diagrams[this.selectedTab].flowId}`, JSON.stringify(this.diagrams[this.selectedTab]));
    },
    readSettingsForDiagram(diagram) {
        let localStoragePositions = localStorage.getItem(`node-${diagram.flowId}`);
        if (localStoragePositions) {
            let oldPositions = JSON.parse(localStoragePositions);

            oldPositions.nodes.forEach(p => {
                let nodeIndex = diagram.nodes.findIndex(t => t.id === p.id);

                if (nodeIndex !== -1) {
                    diagram.nodes[nodeIndex].x = p.x;
                    diagram.nodes[nodeIndex].y = p.y;
                }
            });

            diagram.sortNodes();
        }
    },
    getHeightForContainer(nodes) {
        const maxNode = nodes.reduce(function(prev, current) {
            return (prev.y > current.y) ? prev : current
        });

        return maxNode.y + 90;
    },
    getWidthForContainer(nodes) {
        const maxNode = nodes.reduce(function(prev, current) {
            return (prev.x > current.x) ? prev : current
        });

        return maxNode.x + 240;
    },
    onChangeTab(tabNumber) {
        this.readSettingsForDiagram(this.diagrams[tabNumber]);
    }
  },
};
</script>
<style>
  #svg .connector {
    display: none;
    cursor: pointer;
  }

  #chart {
    border: none;
  }

  #chart #position {
    display: none !important;
  }

</style>